import { collection, getDocs, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface FeeInsights {
  totalTransactions: number;
  totalGrossRevenue: number;
  totalFeesCollected: number;
  currentMonthFeesCollected: number;
  averageFeeRate: number;
  serviceBreakdown: Array<{
    service: string;
    transactions: number;
    grossRevenue: number;
    feesCollected: number;
    averageFeeRate: number;
  }>;
}

interface RealPlatformStats {
  totalUsers: number;
  activeUsers: number;
  totalPosts: number;
  totalMessages: number;
  totalTransactions: number;
  platformRevenue: number;
  totalGroups: number;
  totalEvents: number;
  totalMarketplaceItems: number;
  // Extended stats
  totalPages: number;
  totalHotels: number;
  totalApartments: number;
  totalFoodListings: number;
  totalRestaurants: number;
  totalRecipes: number;
  totalTracks: number;
  totalPhotos: number;
  totalAlbums: number;
  totalCampaigns: number;
  totalTickets: number;
  totalTransportTickets: number;
  totalTransportBookings: number;
  totalVideos: number;
  totalFriendships: number;
  totalNotifications: number;
  totalCalls: number;
  totalOrders: number;
  totalClassifieds: number;
  totalTutorials: number;
  totalCoupons: number;
  totalBlockedUsers: number;
  totalLoginHistory: number;
  recentUsers: any[];
  recentPosts: any[];
  recentTransactions: any[];
  systemHealth: 'healthy' | 'warning' | 'critical';
  uptime: string;
  lastUpdated: string;
}

export class SuperAdminService {
  private static toNumber(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }

  private static toDate(value: unknown): Date | null {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'string' || typeof value === 'number') {
      const dt = new Date(value);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }
    if (typeof (value as any)?.toDate === 'function') {
      const dt = (value as any).toDate();
      return dt instanceof Date && !Number.isNaN(dt.getTime()) ? dt : null;
    }
    return null;
  }

  private static getTxnGrossAmount(data: Record<string, unknown>): number {
    return (
      this.toNumber(data.amount) ||
      this.toNumber(data.totalAmount) ||
      this.toNumber(data.total) ||
      this.toNumber(data.price)
    );
  }

  private static getTxnFeeAmount(data: Record<string, unknown>, grossAmount: number): number {
    const explicitFee =
      this.toNumber(data.platformFee) ||
      this.toNumber(data.feeAmount) ||
      this.toNumber(data.transactionFee) ||
      this.toNumber(data.serviceFee) ||
      this.toNumber(data.fee);

    if (explicitFee > 0) return explicitFee;

    const feeRate =
      this.toNumber(data.feeRate) ||
      this.toNumber(data.platformFeeRate) ||
      this.toNumber(data.commissionRate);

    if (feeRate > 0 && grossAmount > 0) {
      return grossAmount * (feeRate / 100);
    }

    return 0;
  }

  private static getTxnServiceKey(data: Record<string, unknown>): string {
    const raw =
      data.serviceType ||
      data.service ||
      data.feature ||
      data.featureType ||
      data.category ||
      data.source ||
      data.type;

    if (typeof raw !== 'string' || raw.trim().length === 0) return 'unknown';
    return raw.trim();
  }

  static async getFeeInsights(): Promise<FeeInsights> {
    try {
      const snapshot = await getDocs(collection(db, 'transactions'));
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      let totalTransactions = 0;
      let totalGrossRevenue = 0;
      let totalFeesCollected = 0;
      let currentMonthFeesCollected = 0;

      const byService = new Map<
        string,
        { service: string; transactions: number; grossRevenue: number; feesCollected: number }
      >();

      snapshot.forEach((docSnap) => {
        const data = (docSnap.data() || {}) as Record<string, unknown>;
        const grossAmount = this.getTxnGrossAmount(data);
        const feeAmount = this.getTxnFeeAmount(data, grossAmount);
        const service = this.getTxnServiceKey(data);
        const createdAt = this.toDate(data.createdAt) || this.toDate(data.timestamp);

        totalTransactions += 1;
        totalGrossRevenue += grossAmount;
        totalFeesCollected += feeAmount;

        if (createdAt && createdAt >= monthStart) {
          currentMonthFeesCollected += feeAmount;
        }

        const row = byService.get(service) || {
          service,
          transactions: 0,
          grossRevenue: 0,
          feesCollected: 0,
        };

        row.transactions += 1;
        row.grossRevenue += grossAmount;
        row.feesCollected += feeAmount;
        byService.set(service, row);
      });

      const serviceBreakdown = Array.from(byService.values())
        .map((row) => ({
          ...row,
          averageFeeRate: row.grossRevenue > 0 ? (row.feesCollected / row.grossRevenue) * 100 : 0,
        }))
        .sort((a, b) => b.feesCollected - a.feesCollected)
        .slice(0, 8);

      const averageFeeRate = totalGrossRevenue > 0 ? (totalFeesCollected / totalGrossRevenue) * 100 : 0;

      return {
        totalTransactions,
        totalGrossRevenue,
        totalFeesCollected,
        currentMonthFeesCollected,
        averageFeeRate,
        serviceBreakdown,
      };
    } catch (error) {
      console.error('Error fetching fee insights:', error);
      return {
        totalTransactions: 0,
        totalGrossRevenue: 0,
        totalFeesCollected: 0,
        currentMonthFeesCollected: 0,
        averageFeeRate: 0,
        serviceBreakdown: [],
      };
    }
  }

  static async getRealPlatformStats(): Promise<RealPlatformStats> {
    try {
      console.log('🚀 Fetching comprehensive platform statistics...');
      
      // Get total users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const totalUsers = usersSnapshot.size;
      console.log('👥 Total Users:', totalUsers);
      
      // Get active users (users who were active in the last 24 hours)
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      let activeUsers = 0;
      try {
        const activeUsersQuery = query(
          collection(db, 'users'),
          where('lastSeen', '>=', oneDayAgo.toISOString())
        );
        const activeUsersSnapshot = await getDocs(activeUsersQuery);
        activeUsers = activeUsersSnapshot.size;
      } catch (error) {
        console.log('Note: lastSeen field may not exist on all users');
        // Fallback: count users with recent activity
        activeUsers = Math.floor(totalUsers * 0.15); // Estimate 15% active
      }
      console.log('🟢 Active Users (24h):', activeUsers);

      // Get total posts
      const postsSnapshot = await getDocs(collection(db, 'posts'));
      const totalPosts = postsSnapshot.size;
      console.log('📝 Total Posts:', totalPosts);

      // Get total messages
      const messagesSnapshot = await getDocs(collection(db, 'messages'));
      const totalMessages = messagesSnapshot.size;
      console.log('💬 Total Messages:', totalMessages);

      // Get total groups
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const totalGroups = groupsSnapshot.size;
      console.log('👨‍👩‍👧‍👦 Total Groups:', totalGroups);

      // Get total events
      const eventsSnapshot = await getDocs(collection(db, 'events'));
      const totalEvents = eventsSnapshot.size;
      console.log('📅 Total Events:', totalEvents);

      // Get marketplace items
      const marketplaceSnapshot = await getDocs(collection(db, 'products'));
      const totalMarketplaceItems = marketplaceSnapshot.size;
      console.log('🛍️ Total Marketplace Items:', totalMarketplaceItems);

      // Get ALL collections for comprehensive stats
      const collectionsData = await Promise.allSettled([
        getDocs(collection(db, 'pages')),
        getDocs(collection(db, 'hotels')),
        getDocs(collection(db, 'apartments')),
        getDocs(collection(db, 'foodListings')),
        getDocs(collection(db, 'restaurants')),
        getDocs(collection(db, 'recipes')),
        getDocs(collection(db, 'tracks')),
        getDocs(collection(db, 'photos')),
        getDocs(collection(db, 'albums')),
        getDocs(collection(db, 'campaigns')),
        getDocs(collection(db, 'tickets')),
        getDocs(collection(db, 'transportTickets')),
        getDocs(collection(db, 'transportBookings')),
        getDocs(collection(db, 'friendships')),
        getDocs(collection(db, 'notifications')),
        getDocs(collection(db, 'calls')),
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'classifieds')),
        getDocs(collection(db, 'tutorials')),
        getDocs(collection(db, 'coupons')),
        getDocs(collection(db, 'blocked_users')),
        getDocs(collection(db, 'login_history')),
      ]);

      const [
        pagesResult, hotelsResult, apartmentsResult, foodListingsResult,
        restaurantsResult, recipesResult, tracksResult, photosResult,
        albumsResult, campaignsResult, ticketsResult, transportTicketsResult,
        transportBookingsResult, friendshipsResult, notificationsResult,
        callsResult, ordersResult, classifiedsResult, tutorialsResult,
        couponsResult, blockedUsersResult, loginHistoryResult
      ] = collectionsData;

      // Safely extract counts
      const totalPages = pagesResult.status === 'fulfilled' ? pagesResult.value.size : 0;
      const totalHotels = hotelsResult.status === 'fulfilled' ? hotelsResult.value.size : 0;
      const totalApartments = apartmentsResult.status === 'fulfilled' ? apartmentsResult.value.size : 0;
      const totalFoodListings = foodListingsResult.status === 'fulfilled' ? foodListingsResult.value.size : 0;
      const totalRestaurants = restaurantsResult.status === 'fulfilled' ? restaurantsResult.value.size : 0;
      const totalRecipes = recipesResult.status === 'fulfilled' ? recipesResult.value.size : 0;
      const totalTracks = tracksResult.status === 'fulfilled' ? tracksResult.value.size : 0;
      const totalPhotos = photosResult.status === 'fulfilled' ? photosResult.value.size : 0;
      const totalAlbums = albumsResult.status === 'fulfilled' ? albumsResult.value.size : 0;
      const totalCampaigns = campaignsResult.status === 'fulfilled' ? campaignsResult.value.size : 0;
      const totalTickets = ticketsResult.status === 'fulfilled' ? ticketsResult.value.size : 0;
      const totalTransportTickets = transportTicketsResult.status === 'fulfilled' ? transportTicketsResult.value.size : 0;
      const totalTransportBookings = transportBookingsResult.status === 'fulfilled' ? transportBookingsResult.value.size : 0;
      const totalFriendships = friendshipsResult.status === 'fulfilled' ? friendshipsResult.value.size : 0;
      const totalNotifications = notificationsResult.status === 'fulfilled' ? notificationsResult.value.size : 0;
      const totalCalls = callsResult.status === 'fulfilled' ? callsResult.value.size : 0;
      const totalOrders = ordersResult.status === 'fulfilled' ? ordersResult.value.size : 0;
      const totalClassifieds = classifiedsResult.status === 'fulfilled' ? classifiedsResult.value.size : 0;
      const totalTutorials = tutorialsResult.status === 'fulfilled' ? tutorialsResult.value.size : 0;
      const totalCoupons = couponsResult.status === 'fulfilled' ? couponsResult.value.size : 0;
      const totalBlockedUsers = blockedUsersResult.status === 'fulfilled' ? blockedUsersResult.value.size : 0;
      const totalLoginHistory = loginHistoryResult.status === 'fulfilled' ? loginHistoryResult.value.size : 0;

      console.log('📊 Extended Platform Stats:', {
        pages: totalPages,
        hotels: totalHotels,
        apartments: totalApartments,
        foodListings: totalFoodListings,
        restaurants: totalRestaurants,
        recipes: totalRecipes,
        music: totalTracks,
        photos: totalPhotos,
        albums: totalAlbums,
        campaigns: totalCampaigns,
        tickets: totalTickets,
        transportTickets: totalTransportTickets,
        transportBookings: totalTransportBookings,
        friendships: totalFriendships,
        notifications: totalNotifications,
        calls: totalCalls,
        orders: totalOrders,
        classifieds: totalClassifieds,
        tutorials: totalTutorials,
        coupons: totalCoupons,
        blockedUsers: totalBlockedUsers,
        loginHistory: totalLoginHistory
      });

      // Count video posts (posts with mediaType video)
      let totalVideos = 0;
      postsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.mediaType === 'video' || (data.media && data.media.some((url: string) => url.includes('.mp4') || url.includes('video')))) {
          totalVideos++;
        }
      });
      console.log('🎥 Total Videos:', totalVideos);

      // Get recent users (last 10)
      const recentUsersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      const recentUsersSnapshot = await getDocs(recentUsersQuery);
      const recentUsers = recentUsersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get recent posts (last 10)
      const recentPostsQuery = query(
        collection(db, 'posts'),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      const recentPostsSnapshot = await getDocs(recentPostsQuery);
      const recentPosts = recentPostsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get transactions (if you have them)
      let totalTransactions = 0;
      let platformRevenue = 0;
      let recentTransactions = [];
      
      try {
        const transactionsSnapshot = await getDocs(collection(db, 'transactions'));
        totalTransactions = transactionsSnapshot.size;
        
        // Calculate total revenue (assuming transactions have amount field)
        transactionsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.amount) {
            platformRevenue += data.amount;
          }
        });

        // Get recent transactions
        const recentTransactionsQuery = query(
          collection(db, 'transactions'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        const recentTransactionsSnapshot = await getDocs(recentTransactionsQuery);
        recentTransactions = recentTransactionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.log('Transactions collection not found or empty');
      }

      return {
        totalUsers,
        activeUsers,
        totalPosts,
        totalMessages,
        totalTransactions,
        platformRevenue,
        totalGroups,
        totalEvents,
        totalMarketplaceItems,
        totalPages,
        totalHotels,
        totalApartments,
        totalFoodListings,
        totalRestaurants,
        totalRecipes,
        totalTracks,
        totalPhotos,
        totalAlbums,
        totalCampaigns,
        totalTickets,
        totalTransportTickets,
        totalTransportBookings,
        totalVideos,
        totalFriendships,
        totalNotifications,
        totalCalls,
        totalOrders,
        totalClassifieds,
        totalTutorials,
        totalCoupons,
        totalBlockedUsers,
        totalLoginHistory,
        recentUsers,
        recentPosts,
        recentTransactions,
        systemHealth: 'healthy',
        uptime: '99.9%',
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error fetching real platform stats:', error);
      throw error;
    }
  }

  static async getRecentActivity(maxResults: number = 20) {
    try {
      const activities = [];

      // Get recent user registrations
      const recentUsersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const recentUsersSnapshot = await getDocs(recentUsersQuery);
      recentUsersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        activities.push({
          id: `user-${doc.id}`,
          type: 'user_registration',
          message: `New user registered: ${data.name || data.email}`,
          timestamp: data.createdAt,
          user: data
        });
      });

      // Get recent posts
      const recentPostsQuery = query(
        collection(db, 'posts'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const recentPostsSnapshot = await getDocs(recentPostsQuery);
      recentPostsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        activities.push({
          id: `post-${doc.id}`,
          type: 'post_created',
          message: `New post created: ${data.content?.substring(0, 50)}...`,
          timestamp: data.createdAt,
          post: data
        });
      });

      // Get recent groups
      const recentGroupsQuery = query(
        collection(db, 'groups'),
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      const recentGroupsSnapshot = await getDocs(recentGroupsQuery);
      recentGroupsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        activities.push({
          id: `group-${doc.id}`,
          type: 'group_created',
          message: `New group created: ${data.name}`,
          timestamp: data.createdAt,
          group: data
        });
      });

      // Sort activities by timestamp
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      return activities.slice(0, maxResults);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      return [];
    }
  }

  static async getUserStats() {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a: any, b: any) => {
          const aCreated = this.toDate(a.createdAt)?.getTime() ?? 0;
          const bCreated = this.toDate(b.createdAt)?.getTime() ?? 0;
          return bCreated - aCreated;
        }) as any[];

      const totalUsers = users.length;
      const adminUsers = users.filter((user: any) => user.isAdmin).length;
      const sellerUsers = users.filter((user: any) => user.isSeller).length;
      const verifiedUsers = users.filter((user: any) => user.isVerified).length;
      const onlineUsers = users.filter((user: any) => user.isOnline).length;

      // Get users by role
      const roleDistribution = {
        user: users.filter((user: any) => user.role === 'user' || !user.role).length,
        admin: users.filter((user: any) => user.role === 'admin').length,
        seller: users.filter((user: any) => user.role === 'seller').length,
        superadmin: users.filter((user: any) => user.role === 'superadmin').length
      };

      return {
        totalUsers,
        adminUsers,
        sellerUsers,
        verifiedUsers,
        onlineUsers,
        roleDistribution,
        users
      };
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  }

  static async getContentStats() {
    try {
      // Get posts stats
      const postsSnapshot = await getDocs(collection(db, 'posts'));
      const posts = postsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Get messages stats
      const messagesSnapshot = await getDocs(collection(db, 'messages'));
      const messages = messagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Get groups stats
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const groups = groupsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        totalPosts: posts.length,
        totalMessages: messages.length,
        totalGroups: groups.length,
        recentPosts: posts.slice(0, 10),
        recentMessages: messages.slice(0, 10),
        recentGroups: groups.slice(0, 10)
      };
    } catch (error) {
      console.error('Error fetching content stats:', error);
      throw error;
    }
  }

  static async getDetailedAnalytics() {
    try {
      console.log('📈 Fetching detailed analytics...');

      // Content breakdown by type
      const postsSnapshot = await getDocs(collection(db, 'posts'));
      let videoCount = 0;
      let imageCount = 0;
      let textCount = 0;
      let mixedCount = 0;

      postsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.mediaType === 'video') videoCount++;
        else if (data.mediaType === 'image') imageCount++;
        else if (data.mediaType === 'mixed') mixedCount++;
        else textCount++;
      });

      // Booking statistics
      const hotelsSnapshot = await getDocs(collection(db, 'hotels'));
      const apartmentsSnapshot = await getDocs(collection(db, 'apartments'));
      const bookings = {
        totalHotels: hotelsSnapshot.size,
        totalApartments: apartmentsSnapshot.size,
        totalBookingListings: hotelsSnapshot.size + apartmentsSnapshot.size
      };

      // Food & Dining
      const foodListingsSnapshot = await getDocs(collection(db, 'foodListings'));
      const restaurantsSnapshot = await getDocs(collection(db, 'restaurants'));
      const recipesSnapshot = await getDocs(collection(db, 'recipes'));
      const food = {
        totalFoodListings: foodListingsSnapshot.size,
        totalRestaurants: restaurantsSnapshot.size,
        totalRecipes: recipesSnapshot.size,
        totalFoodContent: foodListingsSnapshot.size + restaurantsSnapshot.size + recipesSnapshot.size
      };

      // Entertainment
      const tracksSnapshot = await getDocs(collection(db, 'tracks'));
      const photosSnapshot = await getDocs(collection(db, 'photos'));
      const albumsSnapshot = await getDocs(collection(db, 'albums'));
      const entertainment = {
        totalMusic: tracksSnapshot.size,
        totalPhotos: photosSnapshot.size,
        totalAlbums: albumsSnapshot.size,
        totalVideos: videoCount
      };

      // Social Engagement
      const friendshipsSnapshot = await getDocs(collection(db, 'friendships'));
      const callsSnapshot = await getDocs(collection(db, 'calls'));
      const social = {
        totalFriendships: friendshipsSnapshot.size,
        totalCalls: callsSnapshot.size,
        averageFriendsPerUser: 0 // Will calculate below
      };

      // Marketplace Analytics
      const productsSnapshot = await getDocs(collection(db, 'products'));
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      const marketplace = {
        totalProducts: productsSnapshot.size,
        totalOrders: ordersSnapshot.size,
        conversionRate: productsSnapshot.size > 0 ? ((ordersSnapshot.size / productsSnapshot.size) * 100).toFixed(2) + '%' : '0%'
      };

      // Events & Campaigns
      const eventsSnapshot = await getDocs(collection(db, 'events'));
      const campaignsSnapshot = await getDocs(collection(db, 'campaigns'));
      const eventsAndCampaigns = {
        totalEvents: eventsSnapshot.size,
        totalCampaigns: campaignsSnapshot.size,
        totalPromotions: eventsSnapshot.size + campaignsSnapshot.size
      };

      // Transportation
      const ticketsSnapshot = await getDocs(collection(db, 'tickets'));
      const transportTicketsSnapshot = await getDocs(collection(db, 'transportTickets'));
      const transportBookingsSnapshot = await getDocs(collection(db, 'transportBookings'));
      const transportation = {
        totalTickets: ticketsSnapshot.size,
        totalTransportTickets: transportTicketsSnapshot.size,
        totalTransportBookings: transportBookingsSnapshot.size,
        totalTransportContent: ticketsSnapshot.size + transportTicketsSnapshot.size + transportBookingsSnapshot.size
      };

      // Learning & Support
      const tutorialsSnapshot = await getDocs(collection(db, 'tutorials'));
      const classifiedsSnapshot = await getDocs(collection(db, 'classifieds'));
      const couponsSnapshot = await getDocs(collection(db, 'coupons'));
      const learning = {
        totalTutorials: tutorialsSnapshot.size,
        totalClassifieds: classifiedsSnapshot.size,
        totalCoupons: couponsSnapshot.size
      };

      // Pages
      const pagesSnapshot = await getDocs(collection(db, 'pages'));
      const pages = {
        totalPages: pagesSnapshot.size
      };

      console.log('✅ Detailed analytics compiled successfully');

      return {
        contentBreakdown: {
          videos: videoCount,
          images: imageCount,
          text: textCount,
          mixed: mixedCount,
          total: postsSnapshot.size
        },
        bookings,
        food,
        entertainment,
        social,
        marketplace,
        eventsAndCampaigns,
        transportation,
        learning,
        pages
      };
    } catch (error) {
      console.error('Error fetching detailed analytics:', error);
      return null;
    }
  }

  static async getSystemMetrics() {
    try {
      // Get collection sizes for storage metrics
      const collections = [
        'users', 'posts', 'messages', 'groups', 'events', 'products',
        'hotels', 'apartments', 'foodListings', 'restaurants', 'recipes',
        'tracks', 'photos', 'albums', 'campaigns', 'tickets',
        'friendships', 'notifications', 'calls', 'orders'
      ];

      let totalDocuments = 0;
      const collectionSizes: Record<string, number> = {};

      await Promise.all(
        collections.map(async (collectionName) => {
          try {
            const snapshot = await getDocs(collection(db, collectionName));
            const size = snapshot.size;
            collectionSizes[collectionName] = size;
            totalDocuments += size;
          } catch (error) {
            collectionSizes[collectionName] = 0;
          }
        })
      );

      // Calculate performance metrics
      const now = Date.now();
      const startTime = now;
      
      // Test Firestore response time
      await getDocs(collection(db, 'users'));
      const responseTime = Date.now() - startTime;

      return {
        totalDocuments,
        totalCollections: collections.length,
        collectionSizes,
        performance: {
          responseTime: `${responseTime}ms`,
          status: responseTime < 100 ? 'excellent' : responseTime < 500 ? 'good' : 'slow'
        },
        storage: {
          estimatedSize: `${(totalDocuments * 2).toFixed(2)} KB`, // Rough estimate
          documentsCount: totalDocuments
        }
      };
    } catch (error) {
      console.error('Error fetching system metrics:', error);
      return null;
    }
  }
}